import React, { useState } from 'react';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks/useApiKeys';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { ClipboardDocumentIcon, TrashIcon, KeyIcon } from '@heroicons/react/24/outline';

/**
 * API Keys Management Page
 * Allows users to create, view, and revoke API keys for CLI access
 */
const ApiKeysPage = () => {
  const { data: apiKeys, isLoading, error, refetch } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null); // Stores the newly created key (shown once)
  const [formData, setFormData] = useState({
    name: '',
    expires_in_days: 90
  });

  const handleCreateKey = async (e) => {
    e.preventDefault();

    try {
      const result = await createApiKey.mutateAsync({
        name: formData.name,
        scopes: ['dbt:upload'],
        expires_in_days: formData.expires_in_days || null
      });

      // Show the full key in modal
      setNewKeyData(result.api_key_record);
      setFormData({ name: '', expires_in_days: 90 });

      // Close create form, show key display modal
      setShowCreateModal(false);

      // Refresh list
      refetch();
    } catch (error) {
      console.error('Failed to create API key:', error);
      alert('Failed to create API key: ' + error.message);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await revokeApiKey.mutateAsync(keyId);
      refetch();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      alert('Failed to revoke API key: ' + error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
    alert('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading API keys: {error.message}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <p className="text-secondary-600">
          Manage API keys for CLI access and integrations. Use these keys to upload dbt artifacts
          and access Sparvi programmatically.
        </p>
      </div>

      {/* Create Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <KeyIcon className="h-5 w-5 mr-2" />
          Create New API Key
        </button>
      </div>

      {/* API Keys List */}
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="text-secondary-900">Name</th>
                <th className="text-secondary-900">Key Prefix</th>
                <th className="text-secondary-900">Scopes</th>
                <th className="text-secondary-900">Usage</th>
                <th className="text-secondary-900">Last Used</th>
                <th className="text-secondary-900">Created</th>
                <th className="text-secondary-900">Status</th>
                <th className="text-secondary-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!apiKeys || apiKeys.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-secondary-500">
                    <KeyIcon className="h-12 w-12 mx-auto mb-3 text-secondary-300" />
                    <p className="text-lg mb-2">No API keys yet</p>
                    <p className="text-sm">Create your first API key to get started with the CLI</p>
                  </td>
                </tr>
              ) : (
                apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-secondary-50">
                    <td className="font-medium text-secondary-900">{key.name}</td>
                    <td>
                      <code className="bg-secondary-100 text-secondary-800 px-2 py-1 rounded text-sm">
                        {key.key_prefix}...
                      </code>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {key.scopes?.map((scope, idx) => (
                          <span key={idx} className="badge badge-sm badge-secondary">
                            {scope}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-secondary-700">{key.usage_count || 0} uses</td>
                    <td className="text-secondary-600 text-sm">
                      {key.last_used_at
                        ? new Date(key.last_used_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="text-secondary-600 text-sm">
                      {new Date(key.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      {key.is_active ? (
                        <span className="badge badge-success badge-sm">Active</span>
                      ) : (
                        <span className="badge badge-error badge-sm">Revoked</span>
                      )}
                    </td>
                    <td>
                      {key.is_active && (
                        <button
                          onClick={() => handleRevoke(key.id, key.name)}
                          className="btn btn-ghost btn-sm text-error hover:bg-error hover:text-white"
                          title="Revoke this key"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Using API Keys with the CLI</h3>
        <p className="text-blue-800 text-sm mb-2">
          Once you create an API key, you can use it with the Sparvi CLI to upload dbt artifacts:
        </p>
        <pre className="bg-blue-100 text-blue-900 p-3 rounded text-sm overflow-x-auto">
          export SPARVI_API_KEY=your_api_key_here{'\n'}
          sparvi dbt upload
        </pre>
        <p className="text-blue-700 text-xs mt-2">
          Learn more in the{' '}
          <a href="/docs/dbt-integration" className="underline font-medium">
            dbt Integration Guide
          </a>
        </p>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal modal-open">
          <div className="modal-box bg-white">
            <h3 className="font-bold text-lg text-secondary-900 mb-4">Create New API Key</h3>
            <form onSubmit={handleCreateKey}>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Key Name *</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered bg-white text-secondary-900"
                  placeholder="e.g., CI/CD Pipeline, Local Development"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <label className="label">
                  <span className="label-text-alt text-secondary-600">
                    Choose a descriptive name to identify where this key is used
                  </span>
                </label>
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Expires In (days)</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered bg-white text-secondary-900"
                  placeholder="90"
                  value={formData.expires_in_days}
                  onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) })}
                  min="1"
                  max="365"
                />
                <label className="label">
                  <span className="label-text-alt text-secondary-600">
                    Leave empty for no expiration
                  </span>
                </label>
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-ghost"
                  disabled={createApiKey.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createApiKey.isPending}
                >
                  {createApiKey.isPending ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Show New Key Modal (only shown once after creation) */}
      {newKeyData && (
        <div className="modal modal-open">
          <div className="modal-box bg-white max-w-2xl">
            <h3 className="font-bold text-lg text-secondary-900 mb-2">Your New API Key</h3>
            <div className="alert alert-warning mb-4">
              <span className="text-sm">
                <strong>Save this key now!</strong> You won't be able to see it again.
              </span>
            </div>

            <div className="bg-secondary-100 p-4 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-secondary-700">API Key:</span>
                <button
                  onClick={() => copyToClipboard(newKeyData.api_key)}
                  className="btn btn-xs btn-ghost"
                  title="Copy to clipboard"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy
                </button>
              </div>
              <code className="block bg-white text-secondary-900 p-3 rounded text-sm font-mono break-all">
                {newKeyData.api_key}
              </code>
            </div>

            <div className="mb-4">
              <h4 className="font-semibold text-secondary-900 mb-2">Quick Start:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-secondary-700">
                <li>Copy the API key above</li>
                <li>Set it as an environment variable: <code className="bg-secondary-100 px-1 rounded">export SPARVI_API_KEY=your_key</code></li>
                <li>Run: <code className="bg-secondary-100 px-1 rounded">sparvi dbt upload</code></li>
              </ol>
            </div>

            <div className="modal-action">
              <button
                onClick={() => setNewKeyData(null)}
                className="btn btn-primary"
              >
                I've Saved My Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;
